<template>
  <div
    class="telemetry-fuel-used-parts"
    :class="{ 'telemetry-fuel-used-parts--running-low': fuelLow }"
  >
    <div class="telemetry-fuel-used-parts__header">
      <p class="telemetry-fuel-used-parts__label"><span>Fuel</span></p>
      <p class="telemetry-fuel-used-parts__data">
        <span v-if="fuelLow" class="telemetry-fuel-used-parts__low-label">LOW</span>
        <span v-if="!fuelLow" class="telemetry-fuel-used-parts__number">{{ capacityUsed }}</span>
        <sup v-if="!fuelLow" class="telemetry-fuel-used-parts__unit"><span>L / 80</span></sup>
      </p>
    </div>
    <svg
      class="telemetry-fuel-used-parts__gauge"
      width="145"
      height="14"
      viewBox="0 0 145 14"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <clipPath id="telemetry-fuel-used-parts__gauge__clip">
        <path
          d="M137 0H15.6569C14.596 0 13.5786 0.421427 12.8284 1.17157L4 10H123.686C125.808 10 127.843 9.15715 129.343 7.65685L137 0Z"
          fill="white"
        />
      </clipPath>

      <g clip-path="url(#telemetry-fuel-used-parts__gauge__clip)">
        <rect
          class="telemetry-fuel-used-parts__gauge__meter"
          :width="gaugeWidth"
          height="10"
          x="14"
          y="0"
          transform="skewX(-45)"
          fill="white"
          opacity="0.8"
        ></rect>
      </g>
      <path
        opacity="0.3"
        d="M1 13H124.858C127.51 13 130.054 11.9464 131.929 10.0711L142 0H144L132.636 11.364C130.948 13.0518 128.659 14 126.272 14H0L1 13Z"
        fill="white"
      />
      <path
        opacity="0.2"
        d="M137 0H15.6569C14.596 0 13.5786 0.421427 12.8284 1.17157L4 10H123.686C125.808 10 127.843 9.15715 129.343 7.65685L137 0Z"
        fill="white"
      />
    </svg>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from '@vue/composition-api'
import useTelemetrySectionUtil from '@/components/RaceVideoPage/RaceVideoDataViewPane/hook/useTelemetrySectionUtil'

/**
 * レース動画再生画面 テレメトリー 燃料使用量積算値パーツのコンポーネント
 */
export default defineComponent({
  name: 'TelemetryFuelUsedParts',
  components: {},
  props: {
    /**
     * バリュー値
     */
    value: {
      type: Number,
      required: true,
    },
    /**
     * 表示fpsと受信データ間隔の差分ms
     */
    diffMs: {
      type: Number,
      required: true,
    },
    /**
     * ゲージMAX値
     */
    max: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const { adjustPosition } = useTelemetrySectionUtil()

    // 位置指定を整数化
    adjustPosition('.telemetry-fuel-used-parts')

    // SVGゲージ横幅
    const gaugeSvgWidth = 123

    // 燃料値が設定されているかどうか
    // -1 の場合、テレメトリーデータが取得できていない状態のため、無効状態フラグを立てる
    const invalidFuel = computed(() => props.value === -1)

    /**
     * gaugeWidth
     * ゲージの横幅を算出
     */
    const gaugeWidth = computed(() =>
      invalidFuel.value ? 0 : gaugeSvgWidth - (gaugeSvgWidth * props.value) / props.max,
    )

    /**
     * capacityUsed
     * 使用容量 表示
     */
    const capacityUsed = computed(() => {
      if (invalidFuel.value) {
        return 0
      }
      const num: number = 80 - props.value
      // 稀に小数点以下10数桁までの半端な値になる場合があるため小数点1位までに四捨五入
      return Math.round(num * 10) / 10
    })

    /**
     * fuelLow
     * 使用容量が 75 を超えた場合にtrue判定
     */
    const fuelLow = computed(() => !invalidFuel.value && props.value >= 75)

    return {
      gaugeWidth,
      capacityUsed,
      fuelLow,
    }
  },
  methods: {},
})
</script>

<style lang="scss" scoped>
.telemetry-fuel-used-parts {
  position: absolute;
  top: calc(min(4.2667vw, 1.333em) + 25.5em);
  left: 2em;
  display: flex;
  flex-flow: row wrap;
  align-items: flex-end;
  justify-content: space-between;
  width: 12em;
  color: #fff;

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
    justify-content: space-between;
    width: 100%;
    padding-bottom: 0.45em;
    line-height: 1;
  }

  &__data {
    display: inline-flex;
    flex-flow: row nowrap;
    align-items: flex-start;
    justify-content: flex-end;
    white-space: nowrap;
  }

  &__number {
    font-size: 1.5em;
    letter-spacing: 0.02em;
  }

  &__low-label {
    font-size: 1.5em;
    letter-spacing: 0.02em;
  }

  &__unit {
    display: inline-block;
    margin-left: 0.2em;
    font-size: 0.91667em;
    font-weight: 500;
    opacity: 0.5;

    span {
      font-size: max(1em, 8px);
    }
  }

  &__label {
    display: inline-block;
    padding: 0 0.5em;
    margin-left: 0.8em;
    letter-spacing: 0.08em;
    line-height: 1.1;
    background: transparent;
    color: rgba(255, 255, 255, 80%);

    span {
      font-size: max(1em, 8px);
    }
  }

  &__gauge {
    width: 100%;

    &__meter {
      transition: all linear $telemetryFps;
    }
  }

  &--running-low {
    color: $red;

    .telemetry-fuel-used-parts__gauge {
      path,
      rect {
        fill: $red;
      }
    }
  }
}
</style>

<style lang="scss">
.race-telemetry-section {
  &--landscape-primary,
  &--landscape-secondary {
    .telemetry-fuel-used-parts {
      left: 1em;
      width: 13em;

      &__number,
      &__low-label {
        font-size: 1.55em;
      }

      &__unit {
        font-size: 0.91667em;

        span {
          font-size: 1.125em;
        }
      }

      &__label {
        span {
          font-size: 1.125em;
        }
      }
    }
  }
}
</style>
